import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiEntityStatistics } from '../../../../api/hexa/ApiEntityStatistics';
import { formatEntityLabel } from '../../../../api/hexa/models/EntityStatisticsModels';
import {
  DateRangeFilter,
  EntityIdFilter,
  ResultsCountFilter,
} from '../StatisticsFilters';
import StatisticsRankedList from '../StatisticsRankedList';
import StatisticsSection from '../StatisticsSection';
import TooltipLink, { API_DOCS_URLS } from '../TooltipLink';
import { useMostSearchedStats } from '../useMostSearchedStats';

interface EvidencesTabProps {
  onFetchError: (message: string) => void;
}

const EvidencesTab = ({ onFetchError }: EvidencesTabProps) => {
  const { t } = useTranslation();

  const fetchEvidences = useCallback(
    (
      params: Parameters<typeof ApiEntityStatistics.mostRequestedEvidences>[0]
    ) =>
      ApiEntityStatistics.mostRequestedEvidences(params).then(
        (response) => response.most_requested
      ),
    []
  );

  const evidencesStats = useMostSearchedStats(fetchEvidences, onFetchError);

  return (
    <StatisticsSection
      title={t('entity_statistics.evidences_title')}
      subtitle={t('entity_statistics.evidences_subtitle')}
      subtitleTooltip={
        <Trans
          i18nKey="entity_statistics.evidences_subtitle_tooltip"
          components={{
            evidencesLink: <TooltipLink href={API_DOCS_URLS.entityEvidences} />,
          }}
        />
      }
      icon={<LeaderboardOutlinedIcon />}
      filtersTitle={t('entity_statistics.filters')}
      onResetFilters={evidencesStats.resetFilters}
      isLoading={evidencesStats.isLoading}
      loadingText={t('entity_statistics.evidences_loading')}
      isEmpty={evidencesStats.results.length === 0}
      emptyText={t('entity_statistics.evidences_empty')}
      filters={
        <>
          <DateRangeFilter state={evidencesStats} />
          <ResultsCountFilter state={evidencesStats} />
          <EntityIdFilter state={evidencesStats} />
        </>
      }
    >
      <StatisticsRankedList
        valueLabel={t('entity_statistics.requested_count')}
        actionTooltip={t('entity_statistics.open_in_daily_control')}
        entries={evidencesStats.results.map((entry, index) => ({
          id: `${entry.entity_id}-${index}`,
          label: formatEntityLabel(entry.entity_id, entry.name),
          value: entry.requested_count,
          href: `https://dailycontrol.com/screening/${entry.entity_id}`,
        }))}
      />
    </StatisticsSection>
  );
};

export default EvidencesTab;
